// @ts-nocheck
/*
 * Extensions list — v1 logic preserved end-to-end (pre-check on delete,
 * blocks allocated extensions from selection, hides edit on allocated,
 * bulk-create + create buttons, ACL gates). react-bootstrap chrome
 * replaced with shadcn Card + ConfirmDialog + existing DataTable/toolbar.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import {
  Loader2,
  Pencil,
  Plus,
  Trash2,
  Download,
  RefreshCw,
  Radio,
} from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import Loader from '@/components/partials/components/Loader';
import { API_EXTENSION } from '@/service/url';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { ROUTES, USER_ROLES, MODULE_ACL } from '@/utilities/constant';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const Extension = () => {
  const navigate = useNavigate();
  const [extensionList, setExtensionList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [enableDeleteModal, setEnableDeleteModal] = useState(false);
  const [selectedExtensions, setSelectedExtensions] = useState<any[]>([]);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});
  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] = useState<any>({});

  const fetchExtensions = () => {
    setLoading(true);
    getApiCall(`${API_EXTENSION}?data=all`)
      .then((resp: any) => {
        if (resp) {
          const updatedList = [...resp]
            .map((val, idx) => ({ ...val, key: idx + 1 }))
            .sort((a, b) => {
              const byDate = new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
              if (byDate !== 0) return byDate;
              return Number(b.id) - Number(a.id);
            });
          setExtensionList(updatedList);
        }
      })
      .catch((err) => {
        console.error('Error fetching extensions:', err);
        if (typeof err === 'string' && err.trim())
          toast.error(friendlyApiError(err) || 'Failed to fetch extensions');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const user = getUser();
    if (user.role === USER_ROLES.SUPERADMIN) setIsSuperadmin(true);
    if (user?.acl_settings) setLoggedInUserACL(parseModuleOperations(user.acl_settings.module_operations));
    fetchExtensions();
  }, []);

  useEffect(() => {
    const user = getUser();
    if (user?.acl_settings) {
      setModuleOperationsForReadPermissionCheck(parseModuleOperations(user.acl_settings.module_operations));
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const handleDeleteSelectedExtensions = () => {
    if (selectedExtensions.length === 0) return;
    const selectedAllocated = extensionList.filter(
      (ext) => selectedExtensions.includes(ext.id) && ext.is_allocated === 1,
    );
    if (selectedAllocated.length > 0) {
      toast.error('Unable to delete extensions: extension is allocated');
      setEnableDeleteModal(false);
      setSelectedExtensions([]);
      return;
    }
    postApiCall(API_EXTENSION + '/delete', { extensionIds: selectedExtensions })
      .then(() => {
        toast.success('Extensions deleted successfully');
        fetchExtensions();
        setSelectedExtensions([]);
      })
      .catch((err) => {
        toast.error(
          `Unable to delete extensions: ${friendlyApiError(err) || 'unknown error'}`,
        );
      })
      .finally(() => setEnableDeleteModal(false));
  };

  const handleExport = async () => {
    try {
      setButtonDisable(true);
      exportToCSV({
        fileName: 'extensions-list',
        data: extensionList,
        columns: [
          { header: 'ID', accessor: 'id' },
          { header: 'Username', accessor: (row) => row.username || '-' },
          {
            header: 'Allocation Status',
            accessor: (row) => (row.is_allocated == 1 ? 'Allocated' : 'Available'),
          },
          { header: 'Extension', accessor: (row) => row.extension || '-' },
          { header: 'Description', accessor: (row) => row.description || '-' },
          {
            header: 'Creation Date',
            accessor: (row) =>
              new Date(row.created_at).toLocaleString('en-IN', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
              }),
          },
        ],
      });
    } finally {
      setButtonDisable(false);
    }
  };

  const onCheckboxChange = (checked, extensionId) => {
    const extension = extensionList.find((ext) => ext.id === extensionId);
    if (extension && extension.is_allocated === 1) {
      toast.error('Cannot select allocated extensions. Only unallocated extensions can be deleted.');
      return;
    }
    setSelectedExtensions((prev) => (checked ? [...prev, extensionId] : prev.filter((id) => id !== extensionId)));
  };

  const onHeaderCheckboxChange = (checked) => {
    if (checked) {
      const unallocated = extensionList.filter((ext) => ext.is_allocated === 0).map((ext) => ext.id);
      setSelectedExtensions(unallocated);
    } else {
      setSelectedExtensions([]);
    }
  };

  const canRead = hasPermission(moduleOperationsForReadPermissionCheck, 'Extension', 'read');
  if (Object.keys(moduleOperationsForReadPermissionCheck).length > 0 && !canRead) {
    return (
      <div className="my-12 p-4 text-center text-base">
        No read permission found in assigned ACL
      </div>
    );
  }

  const columns: ColumnDef<any>[] = [
    {
      id: '_select',
      enableSorting: false,
      header: () => (
        <Checkbox
          checked={
            extensionList.length > 0 &&
            extensionList.filter((ext) => ext.is_allocated === 0).length > 0 &&
            extensionList
              .filter((ext) => ext.is_allocated === 0)
              .every((ext) => selectedExtensions.includes(ext.id))
          }
          onCheckedChange={onHeaderCheckboxChange}
        />
      ),
      cell: ({ row }) => {
        const isAllocated = row.original.is_allocated === 1;
        return (
          <Checkbox
            checked={selectedExtensions.includes(row.original.id)}
            onCheckedChange={(v) => onCheckboxChange(v, row.original.id)}
            disabled={isAllocated}
            title={isAllocated ? 'Cannot delete allocated extensions' : ''}
          />
        );
      },
    },
    { accessorKey: 'id', header: 'ID' },
    { accessorKey: 'username', header: 'Username' },
    {
      id: 'allocation',
      header: 'Allocation Status',
      cell: ({ row }) =>
        row.original.is_allocated == 0 ? (
          <span className="inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300">
            Available
          </span>
        ) : (
          <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-inset ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300">
            Allocated
          </span>
        ),
    },
    {
      id: 'extension_desc',
      header: 'Extension (Description)',
      cell: ({ row }) => `${row.original.extension} (${row.original.description || 'No Description'})`,
    },
    {
      accessorKey: 'created_at',
      header: 'Creation Date',
      cell: ({ row }) =>
        new Date(row.original.created_at).toLocaleString('en-IN', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        }),
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => {
        const isAllocated =
          row.original.is_allocated === 1 ||
          row.original.is_allocated === '1' ||
          row.original.is_allocated === true;
        const editRoute = ROUTES.MANAGE_AGENTS.EDIT_EXTENSION.replace(':id', row.original.id);
        return (
          <div className="flex items-center gap-1.5">
            {!isAllocated && (isSuperAdmin || loggedInUserACL[MODULE_ACL.EXTENSION]?.edit) && (
              <button
                type="button"
                title="Edit extension"
                onClick={() => navigate(`/${editRoute}`)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition hover:bg-primary/15"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  const canAdd = isSuperAdmin || loggedInUserACL[MODULE_ACL.EXTENSION]?.add;
  const canDelete = isSuperAdmin || loggedInUserACL?.[MODULE_ACL.EXTENSION]?.delete;

  return (
    <FillPage>
      <PageHeader
        title="Extensions"
        description="SIP extensions provisioned for softphone or hardware endpoints."
        actions={
          <>
            <Button
              variant="outline"
              size="icon"
              onClick={fetchExtensions}
              disabled={loading}
              title="Refresh"
              aria-label="Refresh"
            >
              <RefreshCw className={loading ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
            </Button>
            {extensionList.length > 0 && (
              <Button
                variant="outline"
                onClick={handleExport}
                disabled={buttonDisable}
              >
                <Download className="h-4 w-4" />
                Export
              </Button>
            )}
            {canAdd && (
              <>
                <Button
                  variant="outline"
                  onClick={() =>
                    navigate(`/${ROUTES.MANAGE_AGENTS.BULK_CREATE_EXTENSION}`)
                  }
                >
                  Bulk create
                </Button>
                <Button onClick={() => navigate('/create-extension')}>
                  <Plus className="h-4 w-4" />
                  Create extension
                </Button>
              </>
            )}
          </>
        }
      />

      {loading ? (
        <Loader />
      ) : (
        <>
          {canDelete && selectedExtensions.length > 0 && (
            <div
              className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-primary/[0.06] px-3 py-2"
              style={{
                boxShadow:
                  '4px 4px 10px rgba(163, 177, 198, 0.28), -4px -4px 10px rgba(255, 255, 255, 0.85)',
              }}
            >
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/25">
                <Radio className="h-3 w-3" />
                {selectedExtensions.length} selected
              </span>
              <button
                type="button"
                onClick={() => setEnableDeleteModal(true)}
                className="inline-flex items-center gap-1.5 rounded-md bg-rose-100 px-2.5 py-1.5 text-xs font-semibold text-rose-700 ring-1 ring-inset ring-rose-200 transition hover:bg-rose-200 dark:bg-rose-950/40 dark:text-rose-300"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </button>
              <button
                type="button"
                onClick={() => setSelectedExtensions([])}
                className="ml-auto text-[11px] font-semibold text-muted-foreground transition hover:text-foreground"
              >
                Clear
              </button>
            </div>
          )}

          <DataTable
            tableId="extensions"
            columns={columns}
            data={extensionList}
            pageSize={10}
            emptyTitle="No extensions"
            disableExport
            initialSorting={[{ id: 'created_at', desc: true }]}
          />
        </>
      )}

      <ConfirmDialog
        open={enableDeleteModal}
        onOpenChange={setEnableDeleteModal}
        title="Delete extensions?"
        description="Selected extensions will be permanently removed. Allocated extensions are automatically skipped."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        tone="destructive"
        onConfirm={handleDeleteSelectedExtensions}
      />
    </FillPage>
  );
};

export default Extension;
